import { useState, useEffect, useCallback } from 'react';
import './Billing.css';

// Types
interface Plan {
  name: string;
  monthlyFee: number;
  includedCU: number;
  overageRate: number;
}

interface UsageMetric {
  name: string;
  used: number;
  included: number;
  unit: string;
  cost: number;
}

interface DailyUsage {
  date: string;
  computeUnits: number;
  cost: number;
}

interface Invoice {
  id: string;
  date: string;
  period: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  pdfUrl?: string;
}

interface BudgetAlert {
  id: string;
  threshold: number;
  channel: 'email' | 'slack';
  enabled: boolean;
}

interface BillingData {
  plan: Plan;
  currentPeriod: {
    start: string;
    end: string;
    daysRemaining: number;
  };
  usage: UsageMetric[];
  dailyUsage: DailyUsage[];
  totalCost: number;
  projectedCost: number;
  invoices: Invoice[];
  budgetAlerts: BudgetAlert[];
  paymentMethod: {
    type: string;
    last4: string;
    expires: string;
  };
}

// Mock data
function generateMockBillingData(): BillingData {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const daysRemaining = Math.ceil((endOfMonth.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  // Generate daily usage for current month
  const dailyUsage: DailyUsage[] = [];
  for (let d = new Date(startOfMonth); d <= now; d.setDate(d.getDate() + 1)) {
    const cu = Math.floor(25000 + Math.random() * 15000);
    dailyUsage.push({
      date: new Date(d).toISOString().split('T')[0],
      computeUnits: cu,
      cost: cu * 0.00008,
    });
  }

  const totalCU = dailyUsage.reduce((sum, d) => sum + d.computeUnits, 0);
  const totalCost = dailyUsage.reduce((sum, d) => sum + d.cost, 0);

  return {
    plan: {
      name: 'Professional',
      monthlyFee: 99,
      includedCU: 1000000,
      overageRate: 0.08,
    },
    currentPeriod: {
      start: startOfMonth.toISOString().split('T')[0],
      end: endOfMonth.toISOString().split('T')[0],
      daysRemaining,
    },
    usage: [
      { name: 'Compute Units', used: totalCU, included: 1000000, unit: 'CU', cost: Math.max(0, (totalCU - 1000000) * 0.00008) },
      { name: 'Storage', used: 2.4, included: 10, unit: 'GB', cost: 0 },
      { name: 'Bandwidth', used: 8.7, included: 50, unit: 'GB', cost: 0 },
      { name: 'API Requests', used: 145000, included: 500000, unit: 'req', cost: 0 },
    ],
    dailyUsage,
    totalCost: 99 + totalCost, // Base + usage
    projectedCost: 99 + (totalCost / (now.getDate()) * endOfMonth.getDate()),
    invoices: [
      { id: 'inv_001', date: '2024-12-01', period: 'November 2024', amount: 127.50, status: 'paid' },
      { id: 'inv_002', date: '2024-11-01', period: 'October 2024', amount: 112.30, status: 'paid' },
      { id: 'inv_003', date: '2024-10-01', period: 'September 2024', amount: 99.00, status: 'paid' },
    ],
    budgetAlerts: [
      { id: 'alert_1', threshold: 80, channel: 'email', enabled: true },
      { id: 'alert_2', threshold: 100, channel: 'slack', enabled: true },
    ],
    paymentMethod: {
      type: 'card',
      last4: '4242',
      expires: '12/26',
    },
  };
}

// Components
function UsageBar({ metric }: { metric: UsageMetric }) {
  const percentage = Math.min(100, (metric.used / metric.included) * 100);
  const isOverage = metric.used > metric.included;

  return (
    <div className="usage-item">
      <div className="usage-header">
        <span className="usage-name">{metric.name}</span>
        <span className="usage-value">
          {metric.used.toLocaleString()} / {metric.included.toLocaleString()} {metric.unit}
        </span>
      </div>
      <div className="usage-bar">
        <div
          className={`usage-fill ${isOverage ? 'overage' : ''}`}
          style={{ width: `${Math.min(100, percentage)}%` }}
        />
      </div>
      <div className="usage-meta">
        <span className="usage-percent">{percentage.toFixed(1)}% used</span>
        {metric.cost > 0 && <span className="usage-cost">+${metric.cost.toFixed(2)} overage</span>}
      </div>
    </div>
  );
}

function UsageChart({ dailyUsage }: { dailyUsage: DailyUsage[] }) {
  const maxCU = Math.max(...dailyUsage.map((d) => d.computeUnits));

  return (
    <div className="usage-chart">
      <div className="chart-bars">
        {dailyUsage.map((day) => (
          <div
            key={day.date}
            className="chart-bar"
            style={{ height: `${(day.computeUnits / maxCU) * 100}%` }}
            title={`${day.date}: ${day.computeUnits.toLocaleString()} CU ($${day.cost.toFixed(2)})`}
          />
        ))}
      </div>
      <div className="chart-labels">
        <span>{dailyUsage[0]?.date.slice(5)}</span>
        <span>{dailyUsage[dailyUsage.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}

function InvoiceRow({ invoice }: { invoice: Invoice }) {
  return (
    <tr className="invoice-row">
      <td>{invoice.period}</td>
      <td>{invoice.date}</td>
      <td className="amount">${invoice.amount.toFixed(2)}</td>
      <td>
        <span className={`status status-${invoice.status}`}>{invoice.status}</span>
      </td>
      <td>
        <button className="download-btn">PDF</button>
      </td>
    </tr>
  );
}

function BudgetAlertRow({
  alert,
  onToggle,
}: {
  alert: BudgetAlert;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="alert-row">
      <input
        type="checkbox"
        checked={alert.enabled}
        onChange={() => onToggle(alert.id)}
      />
      <span className="alert-threshold">At {alert.threshold}% of budget</span>
      <span className="alert-channel">{alert.channel}</span>
    </div>
  );
}

function CostEstimator() {
  const [circuitCU, setCircuitCU] = useState(1000);
  const [executions, setExecutions] = useState(10000);

  const estimatedCost = (circuitCU * executions * 0.00008);

  return (
    <div className="cost-estimator">
      <h3>Cost Estimator</h3>
      <div className="estimator-inputs">
        <div className="input-group">
          <label>Circuit Compute Units</label>
          <input
            type="number"
            value={circuitCU}
            onChange={(e) => setCircuitCU(Number(e.target.value))}
          />
        </div>
        <div className="input-group">
          <label>Monthly Executions</label>
          <input
            type="number"
            value={executions}
            onChange={(e) => setExecutions(Number(e.target.value))}
          />
        </div>
      </div>
      <div className="estimator-result">
        <span className="result-label">Estimated Monthly Cost</span>
        <span className="result-value">${estimatedCost.toFixed(2)}</span>
      </div>
    </div>
  );
}

// Main Component
export default function Billing() {
  const [data, setData] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'usage' | 'invoices' | 'settings'>('usage');

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setData(generateMockBillingData());
      setLoading(false);
    }, 300);
  }, []);

  const handleToggleAlert = useCallback((id: string) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        budgetAlerts: prev.budgetAlerts.map((a) =>
          a.id === id ? { ...a, enabled: !a.enabled } : a
        ),
      };
    });
  }, []);

  if (loading || !data) {
    return (
      <div className="billing-page loading">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  return (
    <div className="billing-page">
      <div className="page-header">
        <div>
          <h1>Billing & Usage</h1>
          <p className="subtitle">
            {data.plan.name} Plan • {data.currentPeriod.daysRemaining} days remaining in period
          </p>
        </div>
        <button className="upgrade-btn">Upgrade Plan</button>
      </div>

      {/* Summary Cards */}
      <div className="billing-summary">
        <div className="summary-card">
          <span className="card-label">Current Period</span>
          <span className="card-value">${data.totalCost.toFixed(2)}</span>
          <span className="card-meta">Base: ${data.plan.monthlyFee} + Usage</span>
        </div>
        <div className="summary-card projected">
          <span className="card-label">Projected</span>
          <span className="card-value">${data.projectedCost.toFixed(2)}</span>
          <span className="card-meta">End of billing period</span>
        </div>
        <div className="summary-card">
          <span className="card-label">Included CU</span>
          <span className="card-value">{(data.plan.includedCU / 1000000).toFixed(1)}M</span>
          <span className="card-meta">${data.plan.overageRate}/1K overage</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        <button
          className={`tab ${activeTab === 'usage' ? 'active' : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          Usage
        </button>
        <button
          className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices
        </button>
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'usage' && (
          <div className="usage-tab">
            <div className="usage-section">
              <h2>Daily Usage</h2>
              <UsageChart dailyUsage={data.dailyUsage} />
            </div>

            <div className="usage-section">
              <h2>Resource Usage</h2>
              <div className="usage-list">
                {data.usage.map((metric) => (
                  <UsageBar key={metric.name} metric={metric} />
                ))}
              </div>
            </div>

            <CostEstimator />
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="invoices-tab">
            <h2>Invoice History</h2>
            <table className="invoices-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Download</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((invoice) => (
                  <InvoiceRow key={invoice.id} invoice={invoice} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-tab">
            <div className="settings-section">
              <h2>Payment Method</h2>
              <div className="payment-card">
                <span className="card-icon">💳</span>
                <div className="card-details">
                  <span className="card-number">•••• •••• •••• {data.paymentMethod.last4}</span>
                  <span className="card-expires">Expires {data.paymentMethod.expires}</span>
                </div>
                <button className="update-btn">Update</button>
              </div>
            </div>

            <div className="settings-section">
              <h2>Budget Alerts</h2>
              <div className="alerts-list">
                {data.budgetAlerts.map((alert) => (
                  <BudgetAlertRow
                    key={alert.id}
                    alert={alert}
                    onToggle={handleToggleAlert}
                  />
                ))}
              </div>
              <button className="add-alert-btn">+ Add Alert</button>
            </div>

            <div className="settings-section">
              <h2>Plan Details</h2>
              <div className="plan-details">
                <div className="plan-row">
                  <span>Plan</span>
                  <span>{data.plan.name}</span>
                </div>
                <div className="plan-row">
                  <span>Monthly Base</span>
                  <span>${data.plan.monthlyFee}</span>
                </div>
                <div className="plan-row">
                  <span>Included Compute</span>
                  <span>{data.plan.includedCU.toLocaleString()} CU</span>
                </div>
                <div className="plan-row">
                  <span>Overage Rate</span>
                  <span>${data.plan.overageRate}/1K CU</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
